import React, { useCallback, memo, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { List, Tabs, Tab, Collapse } from '@mui/material';
import CollapsibleSwitchListItem from '../../../../../../list-items/CollapsibleSwitchListItem';
import { createActions } from '../../../../store';
import { VISUALIZER_SHAPE } from '../../../../Preview/PreviewDisplayConstants';
import WaveLayerReflectionTypeListItem from './WaveLayerReflectionTypeListItem';
import WaveLayerTypeListItem from './WaveLayerTypeListItem';
import WaveLayerHeightListItem from './WaveLayerHeightListItem';
import WaveLayerStyleListItem from './WaveLayerStyleListItem';
import WaveLayerSmoothListItem from './WaveLayerSmoothListItem';
import WaveLayerInvertListItem from './WaveLayerInvertListItem';
import WaveLayerInwardsListItem from './WaveLayerInwardsListItem';
import WaveLayerHollowCenterListItem from './WaveLayerHollowCenterListItem';
import WaveLayerRotationListItem from './WaveLayerRotationListItem';
import WaveLayerParticleSettingsListItem from './WaveLayerParticleSettingsListItem';
import CondensedListItem from '../../../../../../list-items/CondensedListItem';
import WaveLayerGlowListItem from '../effects/WaveLayerGlowListItem';
import WaveLayerFireListItem from '../effects/WaveLayerFireListItem';
import WaveLayerShadowListItem from '../effects/WaveLayerShadowListItem';
import WaveLayerSpinListItem from './WaveLayerSpinListItem';

const CUSTOMIZE_TAB = {
    SHAPE: 0,
    EFFECTS: 1
};

const WaveLayerCustomListItem = memo(({ enabled, index, shapeIsCircle, cutout, updateWaveLayerCustomOptions }) => {
    const [tab, setTab] = useState(CUSTOMIZE_TAB.SHAPE);

    const handleToggleEnabled = useCallback(value => {
        updateWaveLayerCustomOptions(index, "enabled", value)
    }, [index])

    const handleTabChange = useCallback((_, value) => setTab(value), []);

    const shapePanel = useMemo(() => (
        <>
            <WaveLayerTypeListItem index={index} />
            {/* Direction sits right below Spectrum, matching the order of the global Shape controls.
                It only applies to the circular shape, so it collapses away for flat. */}
            <Collapse in={shapeIsCircle} unmountOnExit>
                <WaveLayerInwardsListItem index={index} />
            </Collapse>
            <WaveLayerStyleListItem index={index} />
            <WaveLayerParticleSettingsListItem index={index} />
            <WaveLayerReflectionTypeListItem index={index} />
            <WaveLayerSmoothListItem index={index} />
            <WaveLayerInvertListItem index={index} />
            {/* Hollow Center only affects bar-style circular waves; FlatWave never reads it. */}
            <Collapse in={shapeIsCircle} unmountOnExit>
                <WaveLayerHollowCenterListItem index={index} />
            </Collapse>
            <CondensedListItem
                elements={[
                    { width: 6, component: <WaveLayerHeightListItem index={index} /> },
                    { width: 6, component: <WaveLayerRotationListItem index={index} /> },
                ]}
            />
        </>
    ), [index, shapeIsCircle])

    // Spin lives at the bottom of the Effects tab, below the visual effects.
    //
    // A Transparent layer paints nothing of its own; all it contributes is a knockout, and that
    // knockout graphic sits outside the containers Glow, Fire and Shadow filter, so none of the three
    // can reach it. They collapse away rather than offer settings that do nothing. Spin stays: the
    // knockout is rotated with the layer, so spinning a transparent layer really does move the hole.
    const effectsPanel = useMemo(() => (
        <>
            <Collapse in={!cutout} unmountOnExit>
                <WaveLayerGlowListItem index={index} />
                <WaveLayerFireListItem index={index} />
                <WaveLayerShadowListItem index={index} />
            </Collapse>
            <WaveLayerSpinListItem index={index} />
        </>
    ), [index, cutout])

    const childComponents = (
        <List dense disablePadding>
            <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
                <Tab label="Shape" />
                <Tab label="Effects" />
            </Tabs>
            {tab === CUSTOMIZE_TAB.SHAPE ? shapePanel : effectsPanel}
        </List>
    )

    return (
        <CollapsibleSwitchListItem
            checked={enabled}
            onChange={handleToggleEnabled}
            text='Customize'
        >
            {childComponents}
        </CollapsibleSwitchListItem>
    )
})

const mapStateToProps = (state, { index }) => {
    const { visualizer } = state.createPreview.present.videoSettings;
    // Transparent is a layer-level toggle that works independently of Customize, and older documents
    // predate it, so an absent value means "not transparent".
    const { enabled, cutout } = visualizer.waveCircles[index].customOptions;
    const shapeIsCircle = visualizer.shape === VISUALIZER_SHAPE.CIRCLE;
    return { enabled, shapeIsCircle, cutout: cutout === true };
}

const mapDispatchToProps = (dispatch) => ({
    updateWaveLayerCustomOptions: (index, key, value) => dispatch(createActions.updateWaveLayerCustomOptions(index, key, value))
});

export default connect(mapStateToProps, mapDispatchToProps)(WaveLayerCustomListItem);
